import { Typography, CircularProgress, Grid, Chip } from "@material-ui/core";
import { Breadcrumb } from "components/breadCrumb";
import { PageTitle } from "components/pageTitle";
import React, { useEffect } from "react";
import { useParams } from "react-router";
import { Wrapper } from "components/wrapper";
import { DetailCard, DetailField, DetailSectionTitle } from "components/detailPage";
import { DocumentList } from "components/documentList";
import { ExportButtons } from "components/exportButtons";
import { useContratoDetailsState } from "./context/contrato-details-context";
import { useContratoDetailsController } from "./context/contrato-details-controller";

const formatCurrency = (value?: number | null) =>
  value !== undefined && value !== null
    ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '-';

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('pt-BR') : '-';

export function ContratoDetailsPage() {
  const { contratoDetails, loading, error } = useContratoDetailsState();
  const { loadContratoDetails, clearContratoDetails, exportContratoDetail } = useContratoDetailsController();
  let { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (id) {
      loadContratoDetails(parseInt(id));
    }

    return () => {
      clearContratoDetails();
    };
  }, [id, loadContratoDetails, clearContratoDetails]);

  if (loading) {
    return (
      <Wrapper>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </div>
      </Wrapper>
    );
  }

  if (error) {
    return (
      <Wrapper>
        <Typography color="error" variant="h6">{error}</Typography>
      </Wrapper>
    );
  }

  if (!contratoDetails) {
    return (
      <Wrapper>
        <Typography variant="h6">Contrato não encontrado.</Typography>
      </Wrapper>
    );
  }

  return (
    <>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Contratos", href: "/contratos" },
          { label: contratoDetails.numContrato },
        ]}
      />
      <PageTitle>CONTRATO {contratoDetails.numContrato}</PageTitle>
      <Wrapper>
        <ExportButtons onExport={(format) => exportContratoDetail(Number(id), format)} />
        <DetailCard>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Chip label={contratoDetails.situacaoContratoName} color="primary" />
            </Grid>

            <Grid item xs={12} sm={4}>
              <DetailField label="Fornecedor" value={contratoDetails.fornecedorName} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DetailField label="Licitação" value={contratoDetails.licitacaoNumero} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DetailField label="Tipo" value={contratoDetails.tipoContratoName} />
            </Grid>

            <Grid item xs={12}>
              <DetailSectionTitle>Vigência e Valor</DetailSectionTitle>
            </Grid>

            <Grid item xs={12} sm={3}>
              <DetailField label="Assinatura" value={formatDate(contratoDetails.dtaAssinatura)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DetailField label="Início da Vigência" value={formatDate(contratoDetails.dtaInicioVigencia)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DetailField label="Fim da Vigência" value={formatDate(contratoDetails.dtaFimVigencia)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DetailField label="Valor" value={formatCurrency(contratoDetails.valorContrato)} />
            </Grid>

            <Grid item xs={12}>
              <DetailSectionTitle>Detalhes</DetailSectionTitle>
            </Grid>

            <Grid item xs={12}>
              <DetailField label="Objeto" value={contratoDetails.textoObjetoContrato} />
            </Grid>

            {contratoDetails.pncpUrl && (
              <Grid item xs={12}>
                <DetailField
                  label="PNCP"
                  value={<a href={contratoDetails.pncpUrl} target="_blank" rel="noreferrer">{contratoDetails.pncpUrl}</a>}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <DetailSectionTitle>Documentos</DetailSectionTitle>
              <DocumentList documents={contratoDetails.documentos} />
            </Grid>

            <Grid item xs={12}>
              <DetailSectionTitle>Fiscais Designados</DetailSectionTitle>
              {contratoDetails.fiscais && contratoDetails.fiscais.length > 0 ? (
                contratoDetails.fiscais.map((fiscal) => (
                  <Typography key={fiscal.id} variant="body2" style={{ marginBottom: 8 }}>
                    {fiscal.servidorNome} ({fiscal.servidorCargo})
                    {fiscal.dtaDesignacao ? ` — designado em ${formatDate(fiscal.dtaDesignacao)}` : ''}
                  </Typography>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">Nenhum fiscal designado.</Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <DetailSectionTitle>Termos Aditivos</DetailSectionTitle>
              {contratoDetails.aditivos && contratoDetails.aditivos.length > 0 ? (
                contratoDetails.aditivos.map((aditivo) => (
                  <div key={aditivo.id} style={{ marginBottom: 12 }}>
                    <Typography variant="body2">
                      <strong>Aditivo {aditivo.numAditivo}</strong> — {formatCurrency(aditivo.valorAditivo)} — assinado em {formatDate(aditivo.dtaAssinatura)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Vigência: {formatDate(aditivo.dtaInicioVigencia)} a {formatDate(aditivo.dtaFimVigencia)} | {aditivo.textoObjetoAditivo}
                      {aditivo.pNCPUrl ? (<> — <a href={aditivo.pNCPUrl} target="_blank" rel="noreferrer">documento</a></>) : ''}
                    </Typography>
                  </div>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">Nenhum termo aditivo registrado.</Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <DetailSectionTitle>Pagamentos</DetailSectionTitle>
              {contratoDetails.pagamentos && contratoDetails.pagamentos.length > 0 ? (
                contratoDetails.pagamentos
                  .slice()
                  .sort((a, b) => new Date(a.dtaPagamento).getTime() - new Date(b.dtaPagamento).getTime())
                  .map((pagamento) => (
                    <Typography key={pagamento.id} variant="body2" style={{ marginBottom: 8 }}>
                      {formatCurrency(pagamento.valorPagamento)} — {pagamento.fonteRecursoNome}
                      {pagamento.numEmpenho ? ` | Empenho: ${pagamento.numEmpenho}` : ''}
                      {pagamento.numNotaFiscal ? ` | NF: ${pagamento.numNotaFiscal}` : ''}
                      {` | Vencimento: ${formatDate(pagamento.dtaVencimento)} | Pagamento: ${formatDate(pagamento.dtaPagamento)}`}
                    </Typography>
                  ))
              ) : (
                <Typography variant="body2" color="textSecondary">Nenhum pagamento registrado.</Typography>
              )}
            </Grid>
          </Grid>
        </DetailCard>
      </Wrapper>
    </>
  );
}
